import { apiUrl } from '@monorepo/shared/apiClient/httpClient';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { copyToClipboard } from '@monorepo/shared/utils/copyToClipboard';
import { DocumentFileResponse } from 'mapistry-shared';
import { useCallback, useMemo } from 'react';

type UseDocumentDownloadActionsParams = {
  document: Pick<DocumentFileResponse, 'downloadUrl'>;
};

export function useDocumentDownloadActions({
  document,
}: UseDocumentDownloadActionsParams) {
  const { success } = useToast();
  const downloadUrl = useMemo(
    () => `${apiUrl}${document.downloadUrl}`,
    [document.downloadUrl],
  );

  const handleDownload = useCallback(() => {
    // Hit the authenticated download endpoint on the API host; it 302-redirects
    // to a signed S3 URL with `Content-Disposition: attachment`.
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  }, [downloadUrl]);

  const handleCopyLink = useCallback(async () => {
    // Copy the absolute API URL. Visiting it in a browser hits the
    // authenticated `download` endpoint which 302-redirects to a fresh
    // signed S3 URL with `Content-Disposition: attachment` — so the recipient
    // gets a download instead of an HTML page.
    await copyToClipboard(downloadUrl);
    success('Link to this document has been copied to clipboard.');
  }, [downloadUrl, success]);

  return { handleDownload, handleCopyLink };
}
